import { useMemo, useState } from 'react';
import { 
  Grid, 
  Button, 
  TextField, 
  Box,
  Divider,
  Typography,
  Checkbox,
  FormControlLabel,
  IconButton,
  Tooltip
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import Autocomplete from '@material-ui/lab/Autocomplete';
import useCatalogos from '../../../hooks/useCatalogos';
import { ToastMessage } from '../../../global/ToastMessage';

const AditamentosCustom = ({ 
  customAditamentos = [], 
  onAddCustomAditamento,
  onRemoveCustomAditamento,
  onToggleInclusion
}) => {
  const aditamentosCatalog = useCatalogos("Package/Includes/Custom")
  const [nuevoAditamento, setNuevoAditamento] = useState('');
  const [aditamentoSeleccionado, setAditamentoSeleccionado] = useState(null);
  const [errorDuplicado, setErrorDuplicado] = useState(false);

   const opcionesAutocomplete = useMemo(() => {
    return (aditamentosCatalog || []).map(item => ({
      id: item.IdCatPaqueteAditamentoCustom,
      nombre: item.Descripcion || ''
    }));
  }, [aditamentosCatalog]);

  const aditamentoExiste = useMemo(() => {
    return (nombre) => {
      if (!nombre || typeof nombre !== 'string') return false;
      return (customAditamentos || []).some(
        item => item.nombre && item.nombre.toLowerCase() === nombre.toLowerCase().trim()
      );
    };
  }, [customAditamentos]);

  const handleAddClick = () => {
    try {
      const nombre = (nuevoAditamento || '').trim();
      
      if (nombre && !aditamentoExiste(nombre)) {
        onAddCustomAditamento({
          nombre: nombre,
          incluido: false
        });
        setNuevoAditamento('');
        setErrorDuplicado(false);
      } else {
        setErrorDuplicado(true);
      }
    } catch (error) {
      ToastMessage.fire(
        'Error',
        'El aditamento ya existe',
        'error'
      )
    }
  };

  const handleSelectAditamento = (event, value) => {
    try {
      if (value && typeof value === 'object' && value.nombre && !aditamentoExiste(value.nombre)) {
        onAddCustomAditamento({
          nombre: value.nombre,
          incluido: false
        });
        setAditamentoSeleccionado(null);
        setErrorDuplicado(false);
      } else {
        setErrorDuplicado(true);
      }
    } catch (error) {
      ToastMessage.fire(
        'Error',
        'El aditamento ya existe',
        'error'
      )
    }
  };

  const handleInputChange = (event, newInputValue) => {
    try {
      setNuevoAditamento(newInputValue || '');
      if (errorDuplicado) {
        setErrorDuplicado(false);
      }
    } catch (error) {
      ToastMessage.fire(
        'Error',
        'El aditamento ya existe',
        'error'
      )
    }
  };

  const handleToggleInclusion = (id) => {
    try {
      onToggleInclusion(id);
    } catch (error) {
      console.error('Error en handleToggleInclusion:', error);
    }
  };

  const isAddButtonDisabled = useMemo(() => {
    return !nuevoAditamento?.trim() || 
      aditamentoExiste(nuevoAditamento) ||
      errorDuplicado;
  }, [nuevoAditamento, errorDuplicado, aditamentoExiste]);

  return (
    <Box mt={3}>
      <Divider />
      <Typography variant="subtitle1" style={{ margin: '1rem 0' }}>
        <strong>Aditamentos personalizados</strong>
      </Typography>
      
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={8}>
          <Autocomplete
            freeSolo
            options={opcionesAutocomplete}
            getOptionLabel={(option) => {
              if (typeof option === 'string') return option;
              if (option && option.nombre) return option.nombre;
              return '';
            }}
            value={aditamentoSeleccionado}
            onChange={handleSelectAditamento}
            inputValue={nuevoAditamento}
            onInputChange={handleInputChange}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Buscar o agregar aditamento"
                variant="outlined"
                fullWidth
                error={errorDuplicado}
                helperText={errorDuplicado ? "Este aditamento ya existe" : ""}
              />
            )}
          />
        </Grid>

        <Grid item xs={4}>
          <Tooltip
            title={isAddButtonDisabled ? 
              (aditamentoExiste(nuevoAditamento) ? 
                "Este aditamento ya existe" : 
                "Ingrese un nombre válido") : 
              ""}
          >
            <span> {/* Wrapper para el Tooltip con elementos deshabilitados */}
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleAddClick}
                fullWidth
                style={{ height: '56px' }}
                disabled={isAddButtonDisabled}
              >
                Agregar
              </Button>
            </span>
          </Tooltip>
        </Grid>
      </Grid>

      {customAditamentos.length > 0 && (
        <Box mt={2}>
          <Typography variant="body2" style={{ marginBottom: '0.5rem' }}>
            Aditamentos agregados:
          </Typography>
          <Grid container spacing={1}>
            {customAditamentos.map((aditamento) => (
              <Grid item xs={12} key={aditamento.id}>
                <Box 
                  p={1}
                  border={1} 
                  display="flex" 
                  alignItems="center"
                  borderColor="divider" 
                  borderRadius={4}
                >
                  <FormControlLabel
                    label={aditamento.nombre}
                    control={
                      <Checkbox
                        checked={aditamento.incluido}
                        onChange={() => handleToggleInclusion(aditamento.id)}
                        color="primary"
                      />
                    }
                  />
                  <Box flexGrow={1} />
                  <IconButton
                    size="small" 
                    onClick={() => onRemoveCustomAditamento(aditamento.id)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
    </Box>
  );
};

export default AditamentosCustom;